/**
 * Y-axis horizontal spacing + per-axis band-width sizing. Shared by the new
 * charts ({@link ./cartesianChart}) and the old MetricChart primitives
 * ({@link ../MetricChart/chartPrimitives}) so the two never drift.
 */

// recharts axis props take plain px numbers.
//   outer edge → rotated axis label = 8 (--space-xs, via the Label offset)
//   axis label → tick labels        = 18 (via the axis width below)
//   tick labels → plot area         = 16 (--space-md, via tickMargin + tickSize 0)
export const Y_AXIS_LABEL_OFFSET = 8 // --space-xs
export const Y_AXIS_LABEL_GAP = 18
export const Y_AXIS_TICK_MARGIN = 16 // --space-md
// Half the rotated axis label's glyph box at the tick font — the Label is centred
// on its offset, so this much of it sits on the tick side of that point.
const Y_AXIS_LABEL_HALF = 8
// Fallback band width before tick labels are known (e.g. empty state).
export const Y_AXIS_WIDTH = 64

// Per-character px widths at the 12px tick font (Inter), used to size each Y-axis
// band to its own tick labels so the numbers sit a fixed 12px from the rotated
// axis label no matter how wide they are (recharts pins the label to the band's
// outer edge and the ticks to the plot, so a fixed width can't do this). Digits
// use the default; punctuation is narrower.
const TICK_CHAR_PX: Record<string, number> = {
  '.': 3.4,
  ',': 3.4,
  ' ': 3.4,
  '-': 4.4,
  '%': 7.2,
  k: 7.2,
  K: 7.6,
  M: 9.6,
}
const tickLabelWidth = (label: string): number => {
  let width = 0
  for (const ch of label) width += TICK_CHAR_PX[ch] ?? 7.5 // digits ≈ 7.5px at 12px
  return width
}

/**
 * Band width for a Y-axis, sized so its widest tick label sits {@link Y_AXIS_LABEL_GAP}px
 * from the rotated axis label and {@link Y_AXIS_TICK_MARGIN}px from the plot:
 * `offset + labelHalf + gap + widestTick + tickMargin`.
 */
export function computeYAxisWidth(tickLabels: string[]): number {
  const widest = tickLabels.reduce(
    (max, l) => Math.max(max, tickLabelWidth(l)),
    0,
  )
  // No labels (e.g. an 'auto' domain we can't format ahead of render) → fall back.
  if (widest === 0) return Y_AXIS_WIDTH
  return Math.round(
    Y_AXIS_LABEL_OFFSET +
      Y_AXIS_LABEL_HALF +
      Y_AXIS_LABEL_GAP +
      widest +
      Y_AXIS_TICK_MARGIN,
  )
}

/** Formatted labels for `ticks`, for {@link computeYAxisWidth}. Pass every tick:
 *  the widest label is not always an extreme — trimmed half-step labels
 *  (0K · 2.5K · 5K · 7.5K · 10K) are widest in the middle. A non-numeric bound (an
 *  'auto' domain we can't format ahead of render) measures nothing, so the caller
 *  falls back to {@link Y_AXIS_WIDTH}. */
export function axisTickLabels(
  ticks: Array<number | string>,
  format: (value: number) => string,
): string[] {
  const labels: string[] = []
  for (const tick of ticks) {
    if (typeof tick !== 'number') return []
    labels.push(format(tick))
  }
  return labels
}
